import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarCheck, Sparkles, MessageSquare,
  PoundSterling, BarChart3, LogOut, ExternalLink, Search, Download, Trash2, X, Loader2, Check,
  Ticket, Copy, Plus, MessageCircle, Mail, MapPin, FileText, ClipboardList, Tag, Globe, BookOpen,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { LocationsManager } from "@/pages/admin/LocationsManager";
import { BundlesManager } from "@/pages/admin/BundlesManager";
import { ClassRegister } from "@/pages/admin/ClassRegister";
import { ProgrammeManager } from "@/pages/admin/ProgrammeManager";
import { Stars } from "@/components/ReviewForm";
import { Wordmark } from "@/components/Wordmark";

const BLUE = ["#0047FF", "#00D4FF", "#2563EB", "#007FFF", "#1E3A8A", "#9FC6FF", "#0030B3", "#3D7BD9"];
const money = (n) => `£${(n || 0).toLocaleString()}`;

function Metric({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-white/8 bg-ink-800 p-5">
      <p className="label">{label}</p>
      <p className="font-display text-3xl font-bold text-white mt-2">{value}</p>
    </div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-white/8 bg-ink-800 p-5 ${className}`}>
      {title && <h3 className="font-display text-lg font-semibold text-white mb-4">{title}</h3>}
      {children}
    </div>
  );
}

const tooltipStyle = { background: "#050A15", border: "1px solid #1c3570", borderRadius: 8, color: "#E5F0FF", fontSize: 12 };
const tooltipItem = { color: "#E5F0FF" };
const pct = (v, list, key) => { const t = list.reduce((a, d) => a + (d[key] || 0), 0); return t ? Math.round((v / t) * 100) : 0; };
const adminInputCls = "w-full rounded-lg bg-ink-900 border border-white/12 px-3.5 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none text-sm";

function ClearAllButton({ target, label, count, onCleared, testId }) {
  const [busy, setBusy] = useState(false);
  const clear = async () => {
    if (!window.confirm(`Permanently delete ALL ${label}? This sets the count to zero and cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/data/${target}`); onCleared?.(); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={testId} onClick={clear} disabled={busy}
      className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 text-red-300 px-3.5 py-2 text-xs font-medium hover:bg-red-500/10 disabled:opacity-50 transition-colors">
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
      Clear all {label}{count != null ? ` (${count})` : ""}
    </button>
  );
}

function Overview() {
  const [o, setO] = useState(null);
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setO(r.data)).catch(() => setO(false));
    api.get("/admin/charts").then((r) => setC(r.data)).catch(() => {});
  }, []);
  if (o === false) return <p className="text-red-300 text-sm" data-testid="overview-error">Couldn't load the overview — please refresh.</p>;
  if (!o) return <Loader2 className="animate-spin text-cyan" />;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric testId="metric-bookings" label="Bookings this month" value={o.bookings_month} />
        <Metric testId="metric-revenue" label="Revenue this month" value={money(o.revenue_month)} />
        <Metric testId="metric-new-clients" label="New clients this month" value={o.new_clients_month} />
        <Metric testId="metric-returning" label="Returning clients" value={o.returning_clients} />
        <Metric testId="metric-credits" label="Active bundle credits" value={o.active_bundle_credits} />
        <Metric testId="metric-upcoming" label="Upcoming classes" value={o.upcoming_classes} />
        <Metric testId="metric-capacity" label="Avg class capacity" value={`${o.avg_class_capacity}%`} />
        <Metric testId="metric-interest" label="Coming Soon interest" value={o.interest_count} />
      </div>
      {c && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title="Sign-ups over time">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={c.signups_over_time}>
                <CartesianGrid strokeDasharray="3 3" stroke="#16233f" />
                <XAxis dataKey="week" stroke="#8CA3CA" fontSize={11} />
                <YAxis stroke="#8CA3CA" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="signups" stroke="#00D4FF" strokeWidth={2.5} dot={{ fill: "#0047FF" }} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Bookings per class">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={c.bookings_per_class}>
                <CartesianGrid strokeDasharray="3 3" stroke="#16233f" />
                <XAxis dataKey="name" stroke="#8CA3CA" fontSize={11} />
                <YAxis stroke="#8CA3CA" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#0047FF22" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {c.bookings_per_class.map((_, i) => <Cell key={i} fill={BLUE[i % BLUE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Revenue by type">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={c.revenue_by_type} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {c.revenue_by_type.map((_, i) => <Cell key={i} fill={BLUE[i % BLUE.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItem} formatter={(v) => `${money(v)} (${pct(v, c.revenue_by_type, "value")}%)`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 justify-center mt-2">
              {c.revenue_by_type.map((d, i) => (
                <span key={d.name} className="flex items-center gap-1.5 text-xs text-ice/70">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: BLUE[i % BLUE.length] }} /> {d.name}
                </span>
              ))}
            </div>
          </Panel>
          <Panel title="Coming Soon interest by class">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={c.interest_by_class} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#16233f" />
                <XAxis type="number" stroke="#8CA3CA" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#8CA3CA" fontSize={11} width={70} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#0047FF22" }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#00D4FF" />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}
    </div>
  );
}

function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/clients", { params: { search: search || undefined, filter: filter === "all" ? undefined : filter } })
      .then((r) => setClients(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [search, filter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const del = async (c) => {
    if (!window.confirm(`Permanently delete ${c.name || c.email} and all their records? This cannot be undone.`)) return;
    await api.delete(`/admin/clients/${c.id}`);
    setDetail(null); load();
  };
  const exportClient = async (c) => {
    const { data } = await api.get(`/admin/clients/${c.id}/export`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${c.email}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const filters = [
    { k: "all", l: "All" }, { k: "active_bundles", l: "Active bundles" },
    { k: "expiring", l: "Expiring this month" }, { k: "returning", l: "Returning" }, { k: "new", l: "New this month" },
  ];

  return (
    <div data-testid="admin-clients">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mist" />
          <input data-testid="client-search" placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg bg-ink-800 border border-white/12 pl-11 pr-4 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none" />
        </div>
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button key={f.k} data-testid={`client-filter-${f.k}`} onClick={() => setFilter(f.k)}
              className={`rounded-lg px-3.5 py-2.5 text-xs font-medium transition-colors ${filter === f.k ? "bg-electric text-white" : "bg-ink-800 border border-white/10 text-ice/70 hover:text-white"}`}>
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end mb-4">
        <ClearAllButton target="clients" label="clients" count={clients.length} onCleared={load} testId="clear-clients" />
      </div>

      <Panel>
        {loading ? <Loader2 className="animate-spin text-cyan" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/8 text-mist">
                  <th className="py-3 px-2 font-medium">Name</th>
                  <th className="py-3 px-2 font-medium">Email</th>
                  <th className="py-3 px-2 font-medium">Bookings</th>
                  <th className="py-3 px-2 font-medium">Credits</th>
                  <th className="py-3 px-2 font-medium">Joined</th>
                  <th className="py-3 px-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-b border-white/5 hover:bg-ink-700/40">
                    <td className="py-3 px-2 text-white">{c.name || "—"} {c.returning && <span className="ml-1 text-[0.6rem] text-cyan align-middle">RETURNING</span>}</td>
                    <td className="py-3 px-2 text-ice/70">{c.email}</td>
                    <td className="py-3 px-2 text-ice/70">{c.booking_count}</td>
                    <td className="py-3 px-2 text-ice/70">{c.credits_remaining}{c.expiring_this_month && <span className="ml-1 text-[0.6rem] text-yellow-300">EXP SOON</span>}</td>
                    <td className="py-3 px-2 text-ice/60">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="py-3 px-2 flex items-center gap-3">
                      <button data-testid={`client-view-${c.id}`} onClick={() => setDetail(c)} className="text-cyan hover:text-white text-xs">View</button>
                      <button data-testid={`client-delete-row-${c.id}`} onClick={() => del(c)} className="text-red-300/80 hover:text-red-300" title="Remove user"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-mist">No clients found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {detail && <ClientDetail client={detail} onClose={() => setDetail(null)} onDelete={del} onExport={exportClient} />}
    </div>
  );
}

function ClientDetail({ client, onClose, onDelete, onExport }) {
  const [full, setFull] = useState(null);
  useEffect(() => { api.get(`/admin/clients/${client.id}`).then((r) => setFull(r.data)).catch(() => {}); }, [client]);
  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-5 bg-ink-900/85 backdrop-blur-md" onClick={onClose}>
      <div data-testid="client-detail" onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-ink-800 p-7 max-h-[85vh] overflow-y-auto">
        <button aria-label="Close" onClick={onClose} className="absolute top-4 right-4 text-ice/60 hover:text-white"><X size={20} /></button>
        <h3 className="font-display text-2xl font-bold text-white">{client.name || client.email}</h3>
        <p className="text-mist text-sm">{client.email} {client.phone && `· ${client.phone}`}</p>
        <p className="text-mist text-xs mt-1">Location pref: {client.location_preference || "—"} · Joined {(client.created_at || "").slice(0, 10)}</p>

        {full && (
          <>
            <p className="label mt-6 mb-3">Bundle purchases</p>
            {full.bundle_purchases.length ? full.bundle_purchases.map((b) => (
              <div key={b.id} className="rounded-lg bg-ink-900 border border-white/8 p-3 mb-2 text-sm">
                <p className="text-white">{b.bundle_name} · <span className="text-cyan">{b.code}</span></p>
                <p className="text-mist text-xs">{b.credits_remaining}/{b.credits_total} credits · expires {(b.expires_at || "").slice(0, 10)}</p>
              </div>
            )) : <p className="text-mist text-sm">None</p>}

            <p className="label mt-6 mb-3">Booking history</p>
            {full.bookings.length ? full.bookings.map((b) => (
              <div key={b.id} className="rounded-lg bg-ink-900 border border-white/8 p-3 mb-2 text-sm flex justify-between">
                <span className="text-white">{b.class_slug} · {b.class_date ? `${b.class_date} ` : `${b.day} `}{b.time}</span>
                <span className="text-mist text-xs">{b.paid_with_bundle ? "Credit" : money(b.amount)}</span>
              </div>
            )) : <p className="text-mist text-sm">None</p>}
          </>
        )}

        <div className="flex gap-3 mt-7">
          <button data-testid="client-export" onClick={() => onExport(client)} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-white/12 py-2.5 text-sm text-ice hover:text-white"><Download size={15} /> Export data</button>
          <button data-testid="client-delete" onClick={() => onDelete(client)} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-red-500/40 text-red-300 py-2.5 text-sm hover:bg-red-500/10"><Trash2 size={15} /> Delete (GDPR)</button>
        </div>
      </div>
    </div>
  );
}

function Bookings() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/bookings").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ClearAllButton target="bookings" label="bookings" count={rows.length} onCleared={load} testId="clear-bookings" />
      </div>
      <Panel title={`Bookings (${rows.length})`}>
      <div className="overflow-x-auto" data-testid="admin-bookings">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b border-white/8 text-mist">
            <th className="py-3 px-2 font-medium">Client</th><th className="py-3 px-2 font-medium">Class</th>
            <th className="py-3 px-2 font-medium">Slot</th><th className="py-3 px-2 font-medium">Location</th>
            <th className="py-3 px-2 font-medium">Paid</th><th className="py-3 px-2 font-medium">Date</th>
          </tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-b border-white/5 hover:bg-ink-700/40">
                <td className="py-3 px-2 text-white">{b.client_name}</td>
                <td className="py-3 px-2 text-ice/70">{b.class_slug}</td>
                <td className="py-3 px-2 text-ice/70">{b.class_date ? `${b.day} ${b.class_date}` : b.day} {b.time}</td>
                <td className="py-3 px-2 text-ice/70">{b.location_name}</td>
                <td className="py-3 px-2">{b.paid_with_bundle ? <span className="text-cyan">Credit</span> : money(b.amount)}</td>
                <td className="py-3 px-2 text-ice/60">{(b.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
    </div>
  );
}

function Interests() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/interests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const totalInterest = rows.reduce((s, r) => s + (r.count || 0), 0);
  return (
    <div data-testid="admin-interests">
      <div className="flex justify-end mb-4">
        <ClearAllButton target="interests" label="interested people" count={totalInterest} onCleared={load} testId="clear-interests" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <Panel key={r.class_slug}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-white">{r.class_name}</h3>
              <span className="font-display text-3xl font-black text-cyan">{r.count}</span>
            </div>
            <p className="label mt-4 mb-2">Waitlist emails ({r.emails.length})</p>
            <div className="flex flex-wrap gap-2">
              {r.emails.length ? r.emails.map((e, i) => <span key={i} className="text-xs rounded-full bg-ink-900 border border-white/8 px-3 py-1 text-ice/70">{e}</span>) : <span className="text-mist text-sm">No emails yet</span>}
            </div>
          </Panel>
        ))}
        {rows.length === 0 && <p className="text-mist">No interest recorded yet.</p>}
      </div>
    </div>
  );
}

function Contacts() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/contact").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/contact/${id}`, null, { params: { status } }); load(); };
  const togglePublish = async (id) => { await api.post(`/admin/contact/${id}/publish`); load(); };
  return (
    <div className="space-y-3" data-testid="admin-contacts">
      <div className="flex justify-end">
        <ClearAllButton target="contacts" label="messages" count={rows.length} onCleared={load} testId="clear-contacts" />
      </div>
      {rows.map((r) => (
        <Panel key={r.id}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white font-semibold">{r.name} {r.email && <span className="text-mist text-sm font-normal">· {r.email}</span>}</p>
              {r.topic && <span className={`inline-block mt-1.5 text-xs rounded-full px-2.5 py-0.5 ${r.kind === "review" ? "bg-electric/20 text-cyan" : r.topic.toLowerCase().includes("trial") ? "bg-amber-400/15 text-amber-300" : r.topic.includes("1:1") ? "bg-cyan/15 text-cyan" : "bg-white/8 text-ice/70"}`}>{r.topic}{r.kind === "review" && r.published ? " · Published" : ""}</span>}
              {r.kind === "review" && <div className="mt-2"><Stars value={r.rating} size={15} testId={`review-rating-${r.id}`} /></div>}
              <p className="text-ice/75 text-sm mt-2 whitespace-pre-line">{r.message}</p>
              <p className="text-mist text-xs mt-2">{(r.created_at || "").slice(0, 16).replace("T", " ")}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
            {r.kind === "review" && (
              <button data-testid={`review-publish-${r.id}`} onClick={() => togglePublish(r.id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${r.published ? "border border-white/15 text-ice/80 hover:text-red-300" : "bg-electric text-white hover:bg-[#1a5cff]"}`}>
                <Globe size={13} /> {r.published ? "Unpublish" : "Publish on site"}
              </button>
            )}
            <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
              className="rounded-lg bg-ink-900 border border-white/12 px-3 py-2 text-xs text-ice focus:border-cyan/60 outline-none">
              {["New", "Replied", "Archived"].map((s) => <option key={s}>{s}</option>)}
            </select>
            </div>
          </div>
        </Panel>
      ))}
      {rows.length === 0 && <p className="text-mist">No submissions yet.</p>}
    </div>
  );
}

function Financial() {
  const [f, setF] = useState(null);
  const [a, setA] = useState(null);
  const load = () => {
    api.get("/admin/financial").then((r) => setF(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const exportCsv = async () => {
    const res = await api.get("/admin/financial/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const el = document.createElement("a"); el.href = url; el.download = "iamovement_financial.csv"; el.click();
    URL.revokeObjectURL(url);
  };
  if (!f) return <Loader2 className="animate-spin text-cyan" />;
  return (
    <div className="space-y-6" data-testid="admin-financial">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
          <Metric label="Total income" value={money(f.total)} />
          <Metric label="This month" value={money(f.month_total)} />
          <Metric label="Transactions" value={f.transaction_count} />
          <Metric label="Book CTR" value={a ? `${a.book_ctr}%` : "—"} />
        </div>
        <div className="flex items-center gap-3">
          <ClearAllButton target="analytics" label="analytics & finance data" count={f.transaction_count} onCleared={load} testId="clear-analytics" />
          <button data-testid="export-financial" onClick={exportCsv} className="book-cta"><Download size={16} /> Export CSV</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Income by type">
          {Object.entries(f.by_type).map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span className="text-ice/75 capitalize">{k}</span><span className="text-white font-semibold">{money(v)}</span>
            </div>
          ))}
        </Panel>
        <Panel title="Income by location">
          {Object.keys(f.by_location).length ? Object.entries(f.by_location).map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span className="text-ice/75">{k}</span><span className="text-white font-semibold">{money(v)}</span>
            </div>
          )) : <p className="text-mist text-sm">No location income yet.</p>}
        </Panel>
      </div>

      {a && (
        <div className="grid md:grid-cols-2 gap-4">
          <Panel title="Page views">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={a.page_views}>
                <CartesianGrid strokeDasharray="3 3" stroke="#16233f" />
                <XAxis dataKey="page" stroke="#8CA3CA" fontSize={10} />
                <YAxis stroke="#8CA3CA" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#0047FF22" }} />
                <Bar dataKey="views" radius={[4, 4, 0, 0]} fill="#0047FF" />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Traffic sources">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={a.sources} dataKey="views" nameKey="source" innerRadius={50} outerRadius={85} paddingAngle={3}>
                  {a.sources.map((_, i) => <Cell key={i} fill={BLUE[i % BLUE.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItem} formatter={(v) => `${v} views (${pct(v, a.sources, "views")}%)`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 justify-center mt-2">
              {a.sources.map((d, i) => <span key={d.source} data-testid={`traffic-source-${d.source}`} className="flex items-center gap-1.5 text-xs text-ice/70"><span className="h-2.5 w-2.5 rounded-full" style={{ background: BLUE[i % BLUE.length] }} /> {d.source} · {pct(d.views, a.sources, "views")}%</span>)}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

function Codes() {
  const [codes, setCodes] = useState([]);
  const [uses, setUses] = useState(2);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const load = () => api.get("/admin/promo-codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try { await api.post("/admin/promo-codes", { max_uses: Math.max(1, Number(uses) || 1) }); load(); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this code? Anyone holding it will no longer be able to use it.")) return;
    await api.delete(`/admin/promo-codes/${id}`); load();
  };
  const copy = (code) => { navigator.clipboard?.writeText(code); setCopied(code); setTimeout(() => setCopied(null), 1500); };
  const expired = (c) => new Date(c.expires_at) < new Date();

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = (c) => `Here's your free class code for I•AMovement: ${c.code}\n\nBook any class at ${siteUrl}/book\nValid for ${c.max_uses} use${c.max_uses > 1 ? "s" : ""} · expires ${(c.expires_at || "").slice(0, 10)}`;
  const waLink = (c) => `https://wa.me/?text=${encodeURIComponent(shareText(c))}`;
  const mailLink = (c) => `mailto:?subject=${encodeURIComponent("Your free I•AMovement class code")}&body=${encodeURIComponent(shareText(c))}`;

  return (
    <div className="space-y-6" data-testid="admin-codes">
      <Panel title="Create a free-class code">
        <p className="text-ice/70 text-sm mb-4 max-w-2xl">Generate a unique code to give someone for free classes. Set how many times it can be used — it works on any class and automatically expires one month after it's created.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label block mb-1.5">Number of uses</label>
            <input data-testid="promo-uses-input" type="number" min={1} value={uses} onChange={(e) => setUses(e.target.value)}
              className="w-32 rounded-lg bg-ink-900 border border-white/12 px-4 py-2.5 text-ice focus:border-cyan/60 outline-none" />
          </div>
          <button data-testid="promo-generate" onClick={generate} disabled={busy} className="book-cta">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Generate code
          </button>
        </div>
      </Panel>

      <Panel title={`Active codes (${codes.length})`}>
        {codes.length === 0 ? <p className="text-mist text-sm">No codes yet. Generate one above.</p> : (
          <div className="space-y-2">
            {codes.map((c) => {
              const reds = c.redemptions || [];
              return (
                <div key={c.id} data-testid={`promo-row-${c.id}`} className="rounded-lg bg-ink-900 border border-white/8 p-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <button data-testid={`promo-copy-${c.id}`} onClick={() => copy(c.code)} title="Copy code"
                      className="font-display text-xl text-cyan tracking-wide inline-flex items-center gap-2 hover:text-white transition-colors">
                      {c.code} {copied === c.code ? <Check size={15} /> : <Copy size={15} className="opacity-60" />}
                    </button>
                    <span className="text-ice/70 text-sm">{c.uses_count}/{c.max_uses} used</span>
                    <span className={`text-xs ${expired(c) ? "text-red-300" : "text-mist"}`}>
                      {expired(c) ? "Expired" : "Expires"} {(c.expires_at || "").slice(0, 10)}
                    </span>
                    <div className="ml-auto flex items-center gap-1">
                      <a data-testid={`promo-whatsapp-${c.id}`} href={waLink(c)} target="_blank" rel="noreferrer" title="Share via WhatsApp"
                        className="p-2 rounded-lg text-ice/70 hover:text-cyan hover:bg-ink-800 transition-colors"><MessageCircle size={16} /></a>
                      <a data-testid={`promo-email-${c.id}`} href={mailLink(c)} title="Share via email"
                        className="p-2 rounded-lg text-ice/70 hover:text-cyan hover:bg-ink-800 transition-colors"><Mail size={16} /></a>
                      <button data-testid={`promo-delete-${c.id}`} onClick={() => remove(c.id)} className="p-2 rounded-lg text-red-300/80 hover:text-red-300 hover:bg-ink-800 transition-colors" title="Delete code"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  {reds.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/8">
                      <p className="label mb-2">Redeemed by</p>
                      <div className="space-y-1.5">
                        {reds.map((r, i) => (
                          <div key={i} data-testid={`promo-redemption-${c.id}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm">
                            <span className="text-white">{r.name || "—"}</span>
                            <span className="text-ice/60">{r.email}</span>
                            {r.class_slug && <span className="text-cyan/80 text-xs">{r.class_slug}</span>}
                            <span className="text-mist text-xs sm:ml-auto">{(r.at || "").slice(0, 10)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}

function BookingTermsEditor() {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/admin/booking-terms").then((r) => setT(r.data)).catch(() => {}); }, []);
  if (!t) return <Loader2 className="animate-spin text-cyan" />;
  const save = async () => {
    setSaving(true); setSaved(false);
    try { await api.put("/admin/booking-terms", t); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-terms">
      <p className="text-ice/70 text-sm">These terms appear on the booking screen with a mandatory tick-box that customers must accept before paying. Turn it off to hide them.</p>
      <label className="flex items-center gap-2 text-sm text-ice/90 cursor-pointer">
        <input type="checkbox" data-testid="terms-enabled" checked={t.enabled} onChange={(e) => setT({ ...t, enabled: e.target.checked })} className="accent-cyan h-4 w-4" />
        Show terms &amp; require agreement at checkout
      </label>
      <div><label className="label block mb-1.5">Title</label><input data-testid="terms-title" className={adminInputCls} value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} /></div>
      <div><label className="label block mb-1.5">Terms text</label><textarea data-testid="terms-body" rows={18} className={`${adminInputCls} leading-relaxed`} value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} /></div>
      <div><label className="label block mb-1.5">Agreement line (next to the tick-box)</label><input data-testid="terms-checkbox-label" className={adminInputCls} value={t.checkbox_label} onChange={(e) => setT({ ...t, checkbox_label: e.target.value })} /></div>
      <div className="flex items-center gap-3">
        <button data-testid="terms-save" onClick={save} disabled={saving} className="book-cta">{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save terms</button>
        {saved && <span className="text-cyan text-sm inline-flex items-center gap-1"><Check size={15} /> Saved</span>}
      </div>
    </div>
  );
}

const SECTIONS = [
  { k: "overview", l: "Overview", icon: LayoutDashboard, C: Overview },
  { k: "clients", l: "Clients", icon: Users, C: Clients },
  { k: "bookings", l: "Bookings", icon: CalendarCheck, C: Bookings },
  { k: "register", l: "Class register", icon: ClipboardList, C: ClassRegister },
  { k: "locations", l: "Locations & Classes", icon: MapPin, C: LocationsManager },
  { k: "bundles", l: "Bundles", icon: Tag, C: BundlesManager },
  { k: "programme", l: "Training programme", icon: BookOpen, C: ProgrammeManager },
  { k: "terms", l: "Booking Terms", icon: FileText, C: BookingTermsEditor },
  { k: "interests", l: "Interest", icon: Sparkles, C: Interests },
  { k: "contacts", l: "Messages", icon: MessageSquare, C: Contacts },
  { k: "codes", l: "Free codes", icon: Ticket, C: Codes },
  { k: "financial", l: "Finance & Analytics", icon: PoundSterling, C: Financial },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [active, setActive] = useState("overview");

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);
  if (!user) return <div className="min-h-screen bg-ink-900 flex items-center justify-center"><Loader2 className="animate-spin text-cyan" /></div>;
  if (!user.role) { nav("/admin/login"); return null; }

  const Active = SECTIONS.find((s) => s.k === active).C;

  return (
    <div className="min-h-screen bg-ink-900 flex">
      <aside data-testid="admin-sidebar" className="w-16 lg:w-64 shrink-0 border-r border-white/8 bg-ink-900 flex flex-col sticky top-0 h-screen">
        <div className="p-4 lg:p-6 border-b border-white/8"><div className="hidden lg:block"><Wordmark size="text-lg" /></div><div className="lg:hidden text-center text-cyan font-display font-black">I•A</div></div>
        <nav className="flex-1 p-2 lg:p-3 space-y-1">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button key={s.k} data-testid={`admin-nav-${s.k}`} onClick={() => setActive(s.k)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${active === s.k ? "bg-electric/15 text-cyan border border-electric/30" : "text-ice/70 hover:text-white hover:bg-ink-800"}`}>
                <Icon size={18} /> <span className="hidden lg:inline">{s.l}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-2 lg:p-3 border-t border-white/8 space-y-1">
          <a href="/" data-testid="admin-view-site" target="_blank" rel="noreferrer"
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ice/70 hover:text-white hover:bg-ink-800">
            <ExternalLink size={18} /> <span className="hidden lg:inline">View site</span>
          </a>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/admin/login"); }}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ice/70 hover:text-white hover:bg-ink-800">
            <LogOut size={18} /> <span className="hidden lg:inline">Sign out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-5 lg:p-8">
        <header className="flex items-center justify-between mb-7">
          <div>
            <p className="label">Dashboard</p>
            <h1 className="font-display text-2xl lg:text-3xl font-bold text-white mt-1">{SECTIONS.find((s) => s.k === active).l}</h1>
          </div>
          <span className="text-mist text-sm">Signed in as {user.username}</span>
        </header>
        <Active />
      </main>
    </div>
  );
}
